import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import BaseDetails from 'containers/Base/BaseDetails.js';
import { getLoadingState } from 'reducers/ui';
import isEmpty from 'lodash/isEmpty';
import staticText from 'static/text_display.json';
import { translatedKeyValueText } from 'utils/methods/helpers';
import { ROLES } from 'utils/constants';
import {
  eventSelectors,
  followupEventActions,
  followupEventNS,
} from 'containers/Pages/Events';
import FollowupEventForm from './followupEventForm';
import Portal from 'portal/Portal';
import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import OperationalComponent from 'containers/Pages/Events/operational';

const useStyles = makeStyles(() => ({
  EventForm: {
    display: 'flex',
    height: '85vh',
    width: '65vw',
    position: 'relative',
    transform: 'translate(0%, 0%)',
  },
}));

const FollowupEventDetailsPanel = ({
  editable,
  followupEventID,
  followupComponent,
}) => {
  const classes = useStyles();
  const [editMode, setEditMode] = useState(false);
  //----------------Selectors------------------------------------
  const eventDetails = useSelector(state =>
    eventSelectors.getFollowupEventDetailsTransformedSectioned(state),
  );
  const parentEventId = useSelector(state =>
    eventSelectors.getFollowupEventParentID(state),
  );
  const loading = useSelector(state => getLoadingState(state));

  const isSupervisor = useSelector(state => state.user.general.roles);

  // for operational component
  const eventForOperationalComponent = useSelector(state =>
    eventSelectors.getEventDetailsForFollowupOperationalComponent(state),
  );
  const qualifiedToSubmitReview = useSelector(state =>
    eventSelectors.checkIfQualifiedToSubmitReviewFollowup(state),
  );
  const isQualifiedToPublish = useSelector(state =>
    eventSelectors.checkIfQualifiedToPublishFollowup(state),
  );
  const qualifiedToReview = useSelector(state =>
    eventSelectors.checkIfqualifiedToReviewFollowup(state),
  );
  const qualifiedToDistributeAndAssign = useSelector(state =>
    eventSelectors.checkQualifiedToDistributeAndAssignFollowup(state),
  );
  //---------------------------
  const rawEventsData = useSelector(state =>
    eventSelectors.getEventDetailsRaw(state),
  );
  const operation = props => (
    <OperationalComponent
      {...props}
      event={eventForOperationalComponent}
      rawEventsData={rawEventsData}
      qualifiedToSubmitReview={qualifiedToSubmitReview}
      isQualifiedToPublish={isQualifiedToPublish}
      qualifiedToReview={qualifiedToReview}
      qualifiedToDistributeAndAssign={qualifiedToDistributeAndAssign}
    />
  );
  //----------------------Action------------------------------------
  const dispatch = useDispatch();
  const dispatchFetchFollowupEventDetailsByID = useCallback(
    followupEventID => {
      dispatch(
        followupEventActions.fetchFollowupEventDetails({
          id: followupEventID,
          eventType: followupEventNS,
        }),
      );
    },
    [dispatch],
  );
  const resetFollowupEvent = useCallback(
    () =>
      dispatch(
        followupEventActions.setFollowupEventDetails({
          followupEventDetails: {},
        }),
      ),
    [dispatch],
  );
  const resetFollowupEventID = useCallback(
    () =>
      dispatch(
        followupEventActions.setFollowupEventID({
          followupEventID: null,
        }),
      ),
    [dispatch],
  );
  useEffect(() => {
    if (followupEventID && followupEventID) {
      dispatchFetchFollowupEventDetailsByID(followupEventID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupEventID]);

  const onEdit = () => {
    setEditMode(true);
  };

  //the published version of the event should not be editable
  const checkEditable = () => {
    const isDistributionInternal =
      eventDetails?.detailedInfo?.publishing.value.distribution;
    // the person is a supervisor and the distribution is not internal, do not give permission to edit
    if (
      isSupervisor.includes(ROLES.SUPERVISOR.id) &&
      isDistributionInternal !== 'INTERNAL'
    ) {
      return false;
    } else if (
      eventDetails.others.id.value !==
      eventDetails.detailedInfo.publishing.value.publishedId
    ) {
      return editable;
    } else {
      return false;
    }
  };

  const cancel = () => {
    resetFollowupEvent();
    resetFollowupEventID();
  };
  const closeForm = () => {
    setEditMode(false);
  };

  return (
    <>
      <BaseDetails
        header={staticText.operational_view.event.details}
        editable={!isEmpty(eventDetails) && checkEditable()}
        details={eventDetails}
        onEdit={onEdit}
        cancel={cancel}
        loading={loading}
        FollowupButtonComponent={followupComponent}
        operationComponent={!isEmpty(eventDetails) ? operation() : undefined}
        editHoverText={
          eventDetails && Object.keys(eventDetails).length > 0
            ? translatedKeyValueText('edit_value_title', {
                value: eventDetails.noSectionFields.title.value,
              })
            : staticText.operational_view.event.edit_default
        }
      />
      {editMode && (
        <Portal id={'portal'}>
          <Box component={'div'} className={classes.EventForm}>
            <FollowupEventForm
              editMode={editMode}
              closeForm={closeForm}
              disablePresist={true}
              parentEventId={parentEventId}
            />
          </Box>
        </Portal>
      )}
    </>
  );
};

FollowupEventDetailsPanel.propTypes = {
  toggleEditMode: PropTypes.func,
  toggleModal: PropTypes.func,
  editable: PropTypes.bool,
  id: PropTypes.string,
  cancel: PropTypes.func,
  fetchEventById: PropTypes.func,
  event: PropTypes.object,
  setMessage: PropTypes.func,
  resetMsg: PropTypes.func,
  openParentEvent: PropTypes.func,
  parentEventID: PropTypes.string,
  followupEventId: PropTypes.string,
  isSupervisor: PropTypes.array,
  resetEventDetail: PropTypes.func,
  removeSelectedEventId: PropTypes.func,
  followupEventID: PropTypes.string,
  followupComponent: PropTypes.element,
};

export default FollowupEventDetailsPanel;
